import type { ScenarioInput, ScenarioResult, WorkloadRow } from '@exigo/shared';
import { formatMoney, formatPercent } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import { NumberInput, TextInput } from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { dollarsToCents } from '../../lib/money';
import type { ReferenceData } from '../../api/types';
import { WorkloadMix } from './WorkloadMix';
import styles from './AcrCard.module.css';
import calcStyles from './CalculatorPage.module.css';

interface AcrCardProps {
  input: ScenarioInput;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
  reference: ReferenceData;
  /** Null until the first evaluate returns. Only read for the mix and flags. */
  result: ScenarioResult | null;
}

/**
 * Azure consumption entry — the workload breakdown or the single-figure
 * fallback. Every number here is display arithmetic only (a live sum of what
 * was typed); eligibility and payout always come from the next `/evaluate` call,
 * never from anything computed in this component.
 */
export function AcrCard({ input, setInput, reference, result }: AcrCardProps): React.JSX.Element {
  const categoryOptions = [...reference.workloadCategories]
    .sort((a, b) => a.sort - b.sort)
    .map((c) => ({ value: c.key, label: c.label }));

  const multiplier = input.acrPeriod === 'month' ? 12 : 1;

  function updateRow(id: string, patch: Partial<WorkloadRow>): void {
    setInput((p) => ({
      ...p,
      workloads: p.workloads.map((w) => (w.id === id ? { ...w, ...patch } : w)),
    }));
  }

  function addRow(): void {
    setInput((p) => ({
      ...p,
      workloads: [
        ...p.workloads,
        {
          id: `w-${crypto.randomUUID().slice(0, 8)}`,
          name: 'New workload',
          category: 'other',
          amount: 0,
        },
      ],
    }));
  }

  function removeRow(id: string): void {
    setInput((p) => ({ ...p, workloads: p.workloads.filter((w) => w.id !== id) }));
  }

  const isBaselineRow = (category: string): boolean =>
    reference.workloadCategories.find((c) => c.key === category)?.isBaseline === true;

  // Totals for the column as entered (monthly or yearly, whichever is selected)
  // and for the derived yearly column, so the footer balances both.
  const incrementalEntered = input.workloads
    .filter((w) => !isBaselineRow(w.category))
    .reduce((sum, w) => sum + w.amount, 0);
  const baselineEntered = input.workloads
    .filter((w) => isBaselineRow(w.category))
    .reduce((sum, w) => sum + w.amount, 0);

  const incrementalYearly = incrementalEntered * multiplier;
  const baselineYearly = baselineEntered * multiplier;

  return (
    <Card className={calcStyles.section}>
      <div className={styles.headRow}>
        <p className={calcStyles.sectionTitle} style={{ marginBottom: 0 }}>
          Azure consumption
        </p>
        <div className={styles.modeButtons} role="group" aria-label="Azure input mode">
          <button
            type="button"
            className={`${styles.modeButton} ${input.acrMode === 'workload' ? styles.modeButtonActive : ''}`}
            onClick={() => {
              setInput((p) => ({ ...p, acrMode: 'workload' }));
            }}
          >
            Workload breakdown
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${input.acrMode === 'single' ? styles.modeButtonActive : ''}`}
            onClick={() => {
              setInput((p) => ({ ...p, acrMode: 'single' }));
            }}
          >
            Single annual figure
          </button>
        </div>
      </div>

      <div className={styles.controlsRight} style={{ marginBottom: 'var(--space-4)' }}>
        <div className={styles.smallSelect}>
          <Select
            value={input.acrCurrency}
            onChange={(v) => {
              setInput((p) => ({ ...p, acrCurrency: v as ScenarioInput['acrCurrency'] }));
            }}
            options={[
              { value: 'AUD', label: 'AUD' },
              { value: 'USD', label: 'USD' },
            ]}
            ariaLabel="Currency"
          />
        </div>
        {input.acrMode === 'workload' && (
          <div className={styles.smallSelect}>
            <Select
              value={input.acrPeriod}
              onChange={(v) => {
                setInput((p) => ({ ...p, acrPeriod: v as ScenarioInput['acrPeriod'] }));
              }}
              options={[
                { value: 'month', label: 'Per month' },
                { value: 'year', label: 'Per year' },
              ]}
              ariaLabel="Amounts entered as"
            />
          </div>
        )}
        {input.acrMode === 'workload' && (
          <Switch
            label="Include supporting services in scope"
            checked={input.includeSupporting}
            onChange={(v) => {
              setInput((p) => ({ ...p, includeSupporting: v }));
            }}
          />
        )}
      </div>

      {input.acrMode === 'workload' ? (
        <>
          {/* The fixed column widths add up to more than a narrow window can
              give the card, so the table scrolls inside its own box rather
              than clipping or pushing the page sideways. */}
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Workload</th>
                  <th className={styles.catCell}>Qualifying category</th>
                  <th className={styles.amtCell}>
                    {input.acrPeriod === 'month' ? 'Monthly' : 'Yearly'}
                  </th>
                  <th className={styles.yearlyCell}>Yearly</th>
                  <th className={styles.shareCell}>Share</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {input.workloads.map((row) => {
                  const category = reference.workloadCategories.find((c) => c.key === row.category);
                  const isBaseline = category?.isBaseline === true;
                  return (
                    <tr key={row.id} className={isBaseline ? styles.baseRow : undefined}>
                      <td className={styles.nameCell}>
                        <TextInput
                          aria-label="Workload name"
                          value={row.name}
                          onChange={(e) => {
                            updateRow(row.id, { name: e.target.value });
                          }}
                        />
                      </td>
                      <td className={styles.catCell}>
                        <Select
                          value={row.category}
                          onChange={(v) => {
                            updateRow(row.id, { category: v });
                          }}
                          options={categoryOptions}
                          ariaLabel="Qualifying category"
                        />
                      </td>
                      <td className={styles.amtCell}>
                        <NumberInput
                          ariaLabel="Amount"
                          value={row.amount / 100}
                          onValueChange={(v) => {
                            updateRow(row.id, { amount: dollarsToCents(v) });
                          }}
                          min={0}
                        />
                      </td>
                      <td className={styles.yearlyCell}>{formatMoney(row.amount * multiplier)}</td>
                      {/* Share of the incremental total — what the bands are
                          measured on. Baseline rows are excluded from that
                          total, so a percentage would be meaningless for them. */}
                      <td className={styles.shareCell}>
                        {isBaseline ? (
                          <span className={styles.baselineTag}>baseline</span>
                        ) : incrementalYearly > 0 ? (
                          formatPercent(((row.amount * multiplier) / incrementalYearly) * 100)
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.delButton}
                          title="Remove row"
                          aria-label="Remove row"
                          disabled={input.workloads.length <= 1}
                          onClick={() => {
                            removeRow(row.id);
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={styles.tfoot}>
                  <td colSpan={2}>Incremental — this is what the bands measure</td>
                  <td className={styles.amtCell}>{formatMoney(incrementalEntered)}</td>
                  <td className={styles.yearlyCell}>{formatMoney(incrementalYearly)}</td>
                  <td className={styles.shareCell}>{incrementalYearly > 0 ? '100%' : '—'}</td>
                  <td />
                </tr>
                <tr className={styles.tfootSub}>
                  <td colSpan={2}>Existing environment, excluded as baseline</td>
                  <td className={styles.amtCell}>{formatMoney(baselineEntered)}</td>
                  <td className={styles.yearlyCell}>{formatMoney(baselineYearly)}</td>
                  <td className={styles.shareCell}>—</td>
                  <td />
                </tr>
                <tr className={styles.tfootSub}>
                  <td colSpan={2}>Total tenant consumption, including existing</td>
                  <td className={styles.amtCell}>
                    {formatMoney(incrementalEntered + baselineEntered)}
                  </td>
                  <td className={styles.yearlyCell}>
                    {formatMoney(incrementalYearly + baselineYearly)}
                  </td>
                  <td className={styles.shareCell}>—</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={styles.addRow}>
            <Button size="sm" onClick={addRow}>
              + Add workload
            </Button>
          </div>

          <WorkloadMix
            totals={result?.workloadTotals ?? null}
            flags={result?.workloadFlags ?? []}
            categories={reference.workloadCategories}
          />
        </>
      ) : (
        <>
          <div className={styles.manualGrid}>
            <ManualField
              label="Incremental Azure consumption"
              value={input.manualAcr.incremental}
              onChange={(v) => {
                setInput((p) => ({ ...p, manualAcr: { ...p.manualAcr, incremental: v } }));
              }}
            />
            <ManualField
              label="Existing baseline"
              value={input.manualAcr.baseline}
              onChange={(v) => {
                setInput((p) => ({ ...p, manualAcr: { ...p.manualAcr, baseline: v } }));
              }}
            />
            <ManualField
              label="Of that — databases"
              value={input.manualAcr.ofWhichDatabases}
              onChange={(v) => {
                setInput((p) => ({ ...p, manualAcr: { ...p.manualAcr, ofWhichDatabases: v } }));
              }}
            />
            <ManualField
              label="Of that — Fabric / Foundry / GitHub"
              value={input.manualAcr.ofWhichAgentic}
              onChange={(v) => {
                setInput((p) => ({ ...p, manualAcr: { ...p.manualAcr, ofWhichAgentic: v } }));
              }}
            />
            <ManualField
              label="Of that — Defender for Cloud / Sentinel"
              value={input.manualAcr.ofWhichDefenderSentinel}
              onChange={(v) => {
                setInput((p) => ({
                  ...p,
                  manualAcr: { ...p.manualAcr, ofWhichDefenderSentinel: v },
                }));
              }}
            />
          </div>
          <p className={styles.modeNote}>
            Single-figure mode cannot tell workloads apart, so bands read high and the
            recommendation may be wrong. Switch to the workload breakdown before nominating
            anything.
          </p>
        </>
      )}
    </Card>
  );
}

function ManualField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (cents: number) => void;
}): React.JSX.Element {
  return (
    <div className={calcStyles.field}>
      <label className={calcStyles.fieldLabel}>{label}</label>
      <NumberInput
        value={value / 100}
        onValueChange={(v) => {
          onChange(dollarsToCents(v));
        }}
        min={0}
        ariaLabel={label}
      />
    </div>
  );
}
